import { useMemo, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { Link } from "react-router-dom";
import { Power, PowerOff, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  usePendingSimIds,
  useInstanceSims,
  useRefreshSims,
  useSimProfiles,
  useSimUsageForSims,
} from "@/hooks/useSims";
import {
  currentMonthUsageMb,
  formatDataMb,
  profileNetworkIn,
} from "@/lib/sims";
import {
  IccidText,
  ProviderBadge,
  SimNoteLine,
  SimStatusBadge,
} from "./SimBadges";
import { SimActionDialog } from "./SimActionDialog";
import { SimDetailDialog } from "./SimDetailDialog";
import { LinkSimForm } from "./LinkSimForm";
import { SimUsageCharts } from "./SimUsageCharts";
import {
  SimNetworkCell,
  SimNetworkHeaderLabel,
  SimNetworkCountrySelect,
  SimProfileCell,
  useSelectedNetworkCountry,
} from "./SimNetworkCountry";

const THIRTY_TWO_DAYS_MS = 32 * 24 * 60 * 60 * 1000;

/** SIMs of one equipment instance: list, bulk actions, usage charts. */
export function DeviceSimPanel({
  instanceId,
  deviceName,
}: {
  instanceId: string;
  deviceName: string;
}) {
  const { data: sims, isLoading } = useInstanceSims(instanceId);
  const pending = usePendingSimIds();
  const refresh = useRefreshSims();
  const [action, setAction] = useState<"activate" | "deactivate" | null>(null);
  const [detailSimId, setDetailSimId] = useState<string | null>(null);
  // Fixed per mount so the Zero query stays stable.
  const [dailySince] = useState(() => Date.now() - THIRTY_TWO_DAYS_MS);
  const simIds = useMemo(() => sims.map((s) => s.id), [sims]);
  const { monthly, daily } = useSimUsageForSims(simIds, dailySince);
  const { data: profiles } = useSimProfiles();
  const country = useSelectedNetworkCountry();

  const lastUpdate = sims.reduce<number | null>(
    (max, s) =>
      s.usageUpdatedAt && (!max || s.usageUpdatedAt > max)
        ? s.usageUpdatedAt
        : max,
    null,
  );
  const lastSync = sims.reduce<number | null>(
    (max, s) =>
      s.lastSyncedAt && (!max || s.lastSyncedAt > max) ? s.lastSyncedAt : max,
    null,
  );

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading SIMs…</p>;
  }

  return (
    <div className="space-y-4" data-testid={`device-sim-panel-${instanceId}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm text-muted-foreground">
          {sims.length} SIM{sims.length === 1 ? "" : "s"}
          {lastSync &&
            ` · status as of ${formatDistanceToNow(new Date(lastSync), { addSuffix: true })}`}
          {lastUpdate &&
            ` · usage changed ${formatDistanceToNow(new Date(lastUpdate), { addSuffix: true })}`}
        </div>
        <div className="flex flex-wrap gap-2">
          <SimNetworkCountrySelect
            className="h-9 w-44"
            testId="device-sim-network-country"
          />
          <Button
            size="sm"
            variant="outline"
            data-testid="device-activate-all"
            disabled={sims.length === 0}
            onClick={() => setAction("activate")}
          >
            <Power className="mr-1.5 h-4 w-4" /> Activate all
          </Button>
          <Button
            size="sm"
            variant="outline"
            data-testid="device-deactivate-all"
            disabled={sims.length === 0}
            onClick={() => setAction("deactivate")}
          >
            <PowerOff className="mr-1.5 h-4 w-4" /> Deactivate all
          </Button>
          <Button
            size="sm"
            variant="ghost"
            data-testid="device-refresh-sims"
            disabled={sims.length === 0 || refresh.isPending}
            onClick={() => refresh.mutate({ equipmentInstanceId: instanceId })}
          >
            <RefreshCw
              className={`mr-1.5 h-4 w-4 ${refresh.isPending ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
        </div>
      </div>

      <LinkSimForm instanceId={instanceId} deviceSims={sims} />

      {sims.length === 0 ? (
        <p className="rounded-md border p-4 text-center text-sm text-muted-foreground">
          No SIMs assigned. Link one above or assign SIMs from{" "}
          <Link to="/sims" className="underline">
            SIM Cards
          </Link>
          .
        </p>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">Slot</TableHead>
                <TableHead>ICCID</TableHead>
                <TableHead>Provider</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Profile</TableHead>
                {country && (
                  <TableHead className="whitespace-nowrap">
                    <SimNetworkHeaderLabel name={country.name} />
                  </TableHead>
                )}
                <TableHead className="text-right">This month</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sims.map((sim) => (
                <TableRow
                  key={sim.id}
                  data-testid={`device-sim-row-${sim.iccid}`}
                >
                  <TableCell>{sim.slot ?? "–"}</TableCell>
                  <TableCell>
                    <button
                      type="button"
                      className="font-mono text-xs hover:underline"
                      title={sim.iccid}
                      onClick={() => setDetailSimId(sim.id)}
                    >
                      <IccidText iccid={sim.iccid} />
                    </button>
                    <SimNoteLine note={sim.note} />
                  </TableCell>
                  <TableCell>
                    <ProviderBadge provider={sim.provider} />
                  </TableCell>
                  <TableCell>
                    <SimStatusBadge sim={sim} pending={pending.has(sim.id)} />
                  </TableCell>
                  <SimProfileCell sim={sim} />
                  {country && (
                    <SimNetworkCell
                      network={profileNetworkIn(
                        profiles.find((p) => p.id === sim.profileID),
                        country.code,
                      )}
                    />
                  )}
                  <TableCell className="text-right tabular-nums">
                    {formatDataMb(currentMonthUsageMb(sim))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <SimUsageCharts sims={sims} daily={daily} monthly={monthly} />

      <SimActionDialog
        action={action}
        target={action ? { kind: "device", instanceId, deviceName } : null}
        onOpenChange={(open) => !open && setAction(null)}
      />
      <SimDetailDialog
        simId={detailSimId}
        onOpenChange={(open) => !open && setDetailSimId(null)}
      />
    </div>
  );
}
